/**
 * FulfillmentQueue — reusable fulfillment queue page.
 * Wraps the AG Grid Infinite Row Model with fulfillment-specific columns
 * + bulk action bar. Day 50 (item 6) introduced bulk status transitions
 * via bulk_transition_fulfillment_orders RPC; the previously-inline
 * DetailPanel was retired in favor of a dedicated /fulfillment/orders/:id
 * page (FulfillmentOrderDetail.tsx), which is what row-click now opens.
 *
 * Valid transition matrix lives here in code (V1). Will graduate to a
 * DB-driven `fulfillment_status_transitions` table when state-machine
 * work begins (Phase 4+ backlog). See DOMAIN_ARCHITECTURE.md §2.6.
 *
 * Props:
 *   title           — page title
 *   statusFilter    — single status string, array of statuses, or null (all active)
 *   showStatusCol   — show Status column (default false — filtered pages don't need it)
 *   extraColumns    — additional column defs to append (e.g. on_hold_reason)
 *   actionButton    — optional JSX for toolbar Row 1 action
 *   emptyMessage    — empty state text
 */

import { useState, useCallback, useRef, useMemo, useEffect, type JSX, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search, Truck, X } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import ExportButton from '../../components/ui/ExportButton';
import ArbiSelect from '../../components/ui/ArbiSelect';
import { Toast, type ToastState } from '../../components/ui/Toast';
import { useApp } from '../../hooks/useApp';
import { extractBulkEnvelope } from '../../lib/extractEnvelope';
import type { PostgrestError } from '@supabase/supabase-js';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Local types ─────────────────────────────────────────────────────────────

interface FulfillmentOrderRow {
  id: string;
  order_number?: string | null;
  isg_sku?: string | null;
  title?: string | null;
  product_id?: string | null;
  channel?: string | null;
  status: string;
  previous_status?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  marketplace_order_id?: string | null;
  tracking_number?: string | null;
  carrier?: string | null;
  on_hold_reason?: string | null;
  followup_notes?: string | null;
  discrepancy_notes?: string | null;
  updated_at?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
}

// Day 84.6 P4: error type widened to PostgrestError | null to compose with
// extractBulkEnvelope Path 1 translation.
type GenericSupabaseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Status badge colors / labels ────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  new_order: 'bg-info/15 text-info',
  processing: 'bg-accent/15 text-accent',
  review: 'bg-warning/15 text-warning',
  verified: 'bg-success/15 text-success',
  fulfilling: 'bg-accent/15 text-accent',
  picking: 'bg-accent/15 text-accent',
  packed: 'bg-info/15 text-info',
  awaiting_label: 'bg-warning/15 text-warning',
  awaiting_pickup: 'bg-warning/15 text-warning',
  pto: 'bg-warning/15 text-warning',
  on_hold: 'bg-danger/15 text-danger',
  shipped: 'bg-success/15 text-success',
  delivered: 'bg-success/15 text-success',
  channel_fulfilled: 'bg-surface-3 text-ink-muted',
  followup: 'bg-warning/15 text-warning',
  discrepancy: 'bg-danger/15 text-danger',
  cancelled: 'bg-surface-3 text-ink-muted',
};

const STATUS_LABELS: Record<string, string> = {
  new_order: 'New Order',
  processing: 'Processing',
  review: 'Review',
  verified: 'Verified',
  fulfilling: 'Fulfilling',
  picking: 'Picking',
  packed: 'Packed',
  awaiting_label: 'Awaiting Label',
  awaiting_pickup: 'Awaiting Pickup',
  pto: 'PTO',
  on_hold: 'On Hold',
  shipped: 'Shipped',
  delivered: 'Delivered',
  channel_fulfilled: 'Channel Fulfilled',
  followup: 'Followup',
  discrepancy: 'Discrepancy',
  cancelled: 'Cancelled',
};

const CHANNEL_COLORS: Record<string, string> = {
  amazon_fba: 'bg-info/15 text-info',
  amazon_fbm: 'bg-info/15 text-info',
  ebay: 'bg-warning/15 text-warning',
  walmart_self: 'bg-accent/15 text-accent',
  walmart_wfs: 'bg-accent/15 text-accent',
  shopify: 'bg-success/15 text-success',
};
const CHANNEL_LABELS: Record<string, string> = {
  amazon_fba: 'FBA',
  amazon_fbm: 'FBM',
  ebay: 'eBay',
  walmart_self: 'Walmart',
  walmart_wfs: 'WFS',
  shopify: 'Shopify',
};

// ── Valid transition matrix (V1; promote to DB-driven table in Phase 4+) ────
//
// Intersection-based: bulk transition target must be valid for *every* selected
// row's current status. Empty intersection ⇒ disabled bar with guidance.
const VALID_TRANSITIONS: Record<string, string[]> = {
  new_order: ['processing', 'on_hold', 'cancelled'],
  processing: ['review', 'verified', 'on_hold', 'cancelled'],
  review: ['verified', 'on_hold'],
  verified: ['fulfilling', 'picking', 'on_hold'],
  fulfilling: ['picking', 'packed', 'on_hold'],
  picking: ['packed', 'on_hold'],
  packed: ['awaiting_label', 'awaiting_pickup', 'shipped'],
  awaiting_label: ['shipped'],
  awaiting_pickup: ['shipped'],
  shipped: ['delivered'],
  pto: ['fulfilling', 'on_hold'],
  on_hold: [], // resume uses previous_status, not bulk transition
  followup: ['processing', 'on_hold'],
  discrepancy: ['processing', 'on_hold'],
  delivered: ['channel_fulfilled'],
  channel_fulfilled: [],
  cancelled: [],
};

function intersectTransitions(currentStatuses: string[]): string[] {
  if (currentStatuses.length === 0) return [];
  const sets = currentStatuses.map((s) => new Set(VALID_TRANSITIONS[s] || []));
  const [first, ...rest] = sets;
  return [...first].filter((status) => rest.every((set) => set.has(status)));
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Bulk Confirm Modal ─────────────────────────────────────────────────────

interface BulkConfirmModalProps {
  open: boolean;
  selected: FulfillmentOrderRow[];
  newStatus: string | null;
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function BulkConfirmModal({
  open,
  selected,
  newStatus,
  submitting,
  onConfirm,
  onCancel,
}: BulkConfirmModalProps): JSX.Element | null {
  if (!open || !newStatus) return null;
  const visible = selected.slice(0, 10);
  const overflow = selected.length - visible.length;
  const fromStatuses = Array.from(new Set(selected.map((r) => r.status)));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        onClick={onCancel}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        className="relative bg-surface-2 border border-border rounded-xl w-[460px] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Confirm bulk transition"
      >
        <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink-bright">Confirm bulk transition</h3>
          <button
            onClick={onCancel}
            className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 text-xs">
          <p className="text-ink-secondary">
            Transition <span className="font-mono text-ink-bright">{selected.length}</span> order
            {selected.length !== 1 ? 's' : ''} from{' '}
            <span className="font-mono text-ink-bright">
              {fromStatuses.map((s) => STATUS_LABELS[s] || s).join(', ')}
            </span>{' '}
            to{' '}
            <span className="font-mono text-ink-bright">
              {STATUS_LABELS[newStatus] || newStatus}
            </span>
            ?
          </p>
          <div className="bg-surface-3 rounded-md p-3 max-h-40 overflow-y-auto space-y-1">
            {visible.map((row) => (
              <div key={row.id} className="font-mono text-ink-secondary">
                {row.order_number || row.id.slice(0, 8)}
              </div>
            ))}
            {overflow > 0 && <div className="text-ink-muted">+{overflow} more</div>}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border-subtle flex justify-end gap-2">
          <button onClick={onCancel} disabled={submitting} className="arbi-btn-ghost text-xs">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={submitting} className="arbi-btn-primary text-xs">
            {submitting ? 'Transitioning…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

interface FulfillmentQueueProps {
  title: string;
  statusFilter?: string | string[] | null;
  showStatusCol?: boolean;
  extraColumns?: ColDef<FulfillmentOrderRow>[];
  actionButton?: ReactNode;
  emptyMessage?: string;
}

export default function FulfillmentQueue({
  title,
  statusFilter = null,
  showStatusCol = false,
  extraColumns = [],
  actionButton,
  emptyMessage = 'No orders in this queue',
}: FulfillmentQueueProps): JSX.Element {
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer extends the existing dist_rep gate on the bulk
  // action bar. Per §5 matrix viewer view-only on Fulfillment.
  const { isDistRep, isViewer } = useApp();
  const gridRef = useRef<AgGridReact<FulfillmentOrderRow> | null>(null);
  const gridApiRef = useRef<GridApi<FulfillmentOrderRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedRows, setSelectedRows] = useState<FulfillmentOrderRow[]>([]);
  const [bulkTarget, setBulkTarget] = useState<string>('');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const stateRef = useRef<{ searchText: string }>({ searchText });
  stateRef.current = { searchText };

  const rpcStatus = useMemo<string | null>(() => {
    if (!statusFilter) return null;
    if (Array.isArray(statusFilter)) return null;
    return statusFilter;
  }, [statusFilter]);

  const getRowsFn = useCallback(
    async (params: IGetRowsParams): Promise<void> => {
      const { startRow, endRow, successCallback, failCallback } = params;
      const { searchText: st } = stateRef.current;
      try {
        const { data, error } = await callRpc('get_fulfillment_queue', {
          p_status: rpcStatus,
          p_search: st.trim() || null,
          p_limit: endRow - startRow,
          p_offset: startRow,
        });
        if (error) throw new Error(error.message);
        let rows = (data as FulfillmentOrderRow[]) || [];
        if (Array.isArray(statusFilter) && statusFilter.length > 0) {
          rows = rows.filter((r) => statusFilter.includes(r.status));
        }
        if (startRow === 0)
          setTotalCount(
            rows.length < endRow - startRow ? startRow + rows.length : startRow + rows.length + 100
          );
        successCallback(
          rows,
          startRow === 0
            ? rows.length < endRow - startRow
              ? rows.length
              : rows.length + 100
            : undefined
        );
      } catch (e) {
        reportDatasourceFailure(
          title,
          { rpcName: 'get_fulfillment_queue', params: { startRow, endRow, statusFilter } },
          e,
          () => failCallback()
        );
      }
    },
    [rpcStatus, statusFilter, title]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent<FulfillmentOrderRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
  }, [searchText, getRowsFn]);

  const refreshGrid = useCallback((): void => {
    setSelectedRows([]);
    setBulkTarget('');
    if (gridApiRef.current) {
      gridApiRef.current.deselectAll();
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [getRowsFn]);

  const onSelectionChanged = useCallback((): void => {
    if (gridApiRef.current) {
      const rows = gridApiRef.current.getSelectedRows();
      setSelectedRows(rows);
      // Reset bulk target if it's no longer valid for the current selection
      const valid = intersectTransitions(rows.map((r) => r.status));
      if (bulkTarget && !valid.includes(bulkTarget)) setBulkTarget('');
    }
  }, [bulkTarget]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleBulkConfirm = async (): Promise<void> => {
    if (!bulkTarget || selectedRows.length === 0) return;
    if (selectedRows.length > 1000) {
      setToast({
        kind: 'error',
        message: 'Selection exceeds 1000-order bulk cap. Narrow the selection.',
      });
      return;
    }
    setBulkSubmitting(true);
    // Day 84.6 P4.4 — bulk_transition_fulfillment_orders per-row envelope-extract.
    // Pre-Phase-1 returned SETOF fulfillment_orders; post-migration returns
    // TABLE(order_id, ok, data, user_errors) per-row envelope. `data.length` was
    // misleading on partial failures.
    const bulkResult = await callRpc('bulk_transition_fulfillment_orders', {
      p_order_ids: selectedRows.map((r) => r.id),
      p_new_status: bulkTarget,
    });
    setBulkSubmitting(false);
    setBulkConfirmOpen(false);
    const result = extractBulkEnvelope<{ status: string }, 'order_id'>(bulkResult);
    if (!result.isOk && result.rows.length === 0) {
      setToast({
        kind: 'error',
        message: `Bulk transition failed: ${bulkResult.error?.message ?? 'unknown'}`,
      });
      return;
    }
    const successCount = result.rows.filter((r) => r.ok).length;
    const failedCount = result.rows.length - successCount;
    if (result.hasAnyFailure) {
      console.warn(
        '[FulfillmentQueue] bulk-transition partial-failure:',
        result.rows
          .filter((r) => !r.ok)
          .map((r) => ({
            order_id: r.order_id,
            code: r.user_errors[0]?.code,
            message: r.user_errors[0]?.message,
          }))
      );
      setToast({
        kind: 'error',
        message: `${successCount} of ${result.rows.length} orders → ${STATUS_LABELS[bulkTarget] || bulkTarget}; ${failedCount} failed`,
      });
    } else {
      setToast({
        kind: 'success',
        message: `${successCount} order${successCount !== 1 ? 's' : ''} → ${STATUS_LABELS[bulkTarget] || bulkTarget}`,
      });
    }
    refreshGrid();
  };

  // ── Columns ────────────────────────────────────────────────────────────

  const columnDefs = useMemo<ColDef<FulfillmentOrderRow>[]>(() => {
    const cols: ColDef<FulfillmentOrderRow>[] = [
      {
        headerName: 'Order #',
        field: 'order_number',
        width: 130,
        suppressMovable: true,
        resizable: false,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 500,
          color: 'var(--ink-bright)',
        },
      },
      {
        headerName: 'ISG SKU',
        field: 'isg_sku',
        width: 120,
        cellRenderer: (p: ICellRendererParams<FulfillmentOrderRow>) => {
          const sku = p.data?.isg_sku;
          return p.data ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (sku) navigate(`/catalog/products/${sku}`);
              }}
              className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
            >
              {sku}
            </button>
          ) : null;
        },
        cellStyle: { display: 'flex', alignItems: 'center' },
      },
      {
        headerName: 'Title',
        field: 'title',
        flex: 1,
        minWidth: 150,
        cellStyle: { display: 'flex', alignItems: 'center', color: 'var(--ink-primary)' },
      },
      {
        headerName: 'Channel',
        field: 'channel',
        width: 100,
        cellRenderer: (p: ICellRendererParams<FulfillmentOrderRow>) => {
          const ch = p.data?.channel;
          return p.data ? (
            <span
              className={clsx(
                'px-1.5 py-0.5 rounded text-2xs font-medium',
                (ch && CHANNEL_COLORS[ch]) || 'bg-surface-3 text-ink-muted'
              )}
            >
              {(ch && CHANNEL_LABELS[ch]) || ch}
            </span>
          ) : null;
        },
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      },
    ];

    if (showStatusCol) {
      cols.push({
        headerName: 'Status',
        field: 'status',
        width: 120,
        cellRenderer: (p: ICellRendererParams<FulfillmentOrderRow>) => {
          const status = p.data?.status;
          return p.data && status ? (
            <span
              className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', STATUS_COLORS[status])}
            >
              {STATUS_LABELS[status]}
            </span>
          ) : null;
        },
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      });
    }

    cols.push(
      {
        headerName: 'Qty',
        field: 'quantity',
        width: 70,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        },
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'MP Order ID',
        field: 'marketplace_order_id',
        width: 140,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-muted)',
        },
      },
      {
        headerName: 'Tracking #',
        field: 'tracking_number',
        width: 130,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-muted)',
        },
      }
    );

    cols.push(...extraColumns);

    cols.push({
      headerName: 'Updated',
      field: 'updated_at',
      width: 110,
      valueFormatter: (p: ValueFormatterParams<FulfillmentOrderRow>) =>
        relativeTime(p.value as string | null),
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        fontFamily: "'JetBrains Mono', monospace",
        color: 'var(--ink-muted)',
      },
    });

    return cols;
  }, [showStatusCol, extraColumns, navigate]);

  const defaultColDef = useMemo<ColDef<FulfillmentOrderRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 10,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  const validNextStatuses = useMemo(
    () => intersectTransitions(selectedRows.map((r) => r.status)),
    [selectedRows]
  );

  const showBulkBar = !isDistRep && !isViewer && selectedRows.length > 0;

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <BulkConfirmModal
        open={bulkConfirmOpen}
        selected={selectedRows}
        newStatus={bulkTarget || null}
        submitting={bulkSubmitting}
        onConfirm={handleBulkConfirm}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">{title}</h1>
            {totalCount > 0 && (
              <span className="text-xs text-ink-muted">{totalCount.toLocaleString()}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              gridApiRef={gridApiRef}
              fileName={`arbi-${title.toLowerCase().replace(/\s+/g, '-')}`}
            />
            {actionButton}
          </div>
        </div>

        {/* Row 2: bulk action bar (when selection) OR search */}
        {showBulkBar ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-muted">{selectedRows.length} selected</span>
            {validNextStatuses.length === 0 ? (
              <span className="text-2xs text-warning">
                Selected orders have incompatible statuses — narrow the selection to a single status
                group
              </span>
            ) : (
              <>
                <div className="w-44">
                  <ArbiSelect
                    value={bulkTarget}
                    onChange={(v) => setBulkTarget(v)}
                    options={validNextStatuses.map((s) => ({
                      value: s,
                      label: STATUS_LABELS[s] || s,
                    }))}
                    placeholder="Transition to…"
                  />
                </div>
                <button
                  onClick={() => setBulkConfirmOpen(true)}
                  disabled={!bulkTarget || selectedRows.length > 1000}
                  className={clsx(
                    'arbi-btn-primary text-2xs px-2 py-1',
                    (!bulkTarget || selectedRows.length > 1000) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  Apply
                </button>
                {selectedRows.length > 1000 && (
                  <span className="text-2xs text-danger">Exceeds 1000-order bulk cap</span>
                )}
              </>
            )}
            <button
              onClick={() => {
                if (gridApiRef.current) gridApiRef.current.deselectAll();
                setSelectedRows([]);
                setBulkTarget('');
              }}
              className="arbi-btn-ghost text-2xs px-2 py-1 ml-auto"
            >
              Clear
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="relative w-60 ml-auto">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
              />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search orders..."
                className="arbi-input pl-8 pr-3 py-1 text-xs w-full"
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex-1 p-2" style={{ minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            theme={gridTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            cacheBlockSize={100}
            maxBlocksInCache={50}
            selectionColumnDef={SELECTION_COLUMN_DEF}
            rowSelection={{
              mode: 'multiRow',
              enableClickSelection: false,
              checkboxes: true,
              headerCheckbox: false,
            }}
            onGridReady={onGridReady}
            onSelectionChanged={onSelectionChanged}
            animateRows={false}
            headerHeight={36}
            rowHeight={42}
            suppressCellFocus={true}
            suppressHorizontalScroll={true}
            onRowClicked={(e: RowClickedEvent<FulfillmentOrderRow>) => {
              if (e.data) navigate(`/fulfillment/orders/${e.data.id}`);
            }}
            noRowsOverlayComponent={() => (
              <div className="flex flex-col items-center justify-center h-full">
                <Truck size={48} className="text-ink-muted mb-3" />
                <p className="text-sm text-ink-muted">
                  {searchText ? 'No orders match your search' : emptyMessage}
                </p>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
}

export { STATUS_COLORS, STATUS_LABELS, CHANNEL_COLORS, CHANNEL_LABELS };
